/**
 * PublicReportPage
 * Public page for visitors to submit issue reports via QR code
 *
 * No authentication required - uses token-based access.
 * Accessed via /report/:token route.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { Building, Camera, MessageSquare, CheckCircle, AlertCircle, Upload, X } from 'lucide-react';
import { publicReportApi } from '../../api/publicReportApi';
import { useDragDrop } from '../../hooks/useDragDrop';
import { UploadPickerModal } from '../../Components/UploadPickerModal';
import { LIMITS, ALLOWED_MIME_TYPES, PublicReportPhoto, PublicPrivacyPolicy } from '../../types/publicReport';
import PublicReportPrivacyNotice from './PublicReportPrivacyNotice';
import { getDatabase, ref, push } from 'firebase/database';
import './PublicReportPage.css';

interface BuildingInfo {
    name: string;
    address?: string;
    /** The code is placed where non-employees may scan it */
    publicPlacement: boolean;
    privacyPolicy: PublicPrivacyPolicy | null;
}

interface UploadedPhoto {
    key: string;
    file: File;
    preview: string;
    storagePath?: string;
    width?: number;
    height?: number;
    uploading: boolean;
    uploaded: boolean;
    error?: string;
}

/**
 * P2-9: Shared file validation result
 */
interface FileValidationResult {
    valid: boolean;
    error?: string;
}

/**
 * P2-9: Validate a file for upload
 * Shared by drag-drop, paste, and device-picker upload paths.
 */
const validateFile = (file: File, currentPhotoCount: number): FileValidationResult => {
    // Validate file type
    if (!ALLOWED_MIME_TYPES.includes(file.type as typeof ALLOWED_MIME_TYPES[number])) {
        return { valid: false, error: `Filtypen "${file.type}" stöds inte` };
    }

    // Check max photos limit
    if (currentPhotoCount >= LIMITS.MAX_PHOTOS) {
        return { valid: false, error: `Max ${LIMITS.MAX_PHOTOS} bilder tillåtna` };
    }

    // Validate file size
    if (file.size > LIMITS.MAX_PHOTO_SIZE_BYTES) {
        return { valid: false, error: `Bilden "${file.name}" är för stor. Max storlek är 10 MB.` };
    }

    return { valid: true };
};

/**
 * Get image dimensions from a file
 */
const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(img.src);
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
    });
};

/**
 * Public report page for visitors to submit issues via QR code
 */
const PublicReportPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();

    // State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [buildingInfo, setBuildingInfo] = useState<BuildingInfo | null>(null);
    const [description, setDescription] = useState('');
    const [photos, setPhotos] = useState<UploadedPhoto[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [showUploadModal, setShowUploadModal] = useState(false);

    // Unique issue key for this submission (generated once). Firebase auto-ID
    // keeps public issues consistent with internally-created issues. Generated
    // locally — no auth or network round-trip needed.
    const [issueKey] = useState(() => push(ref(getDatabase())).key!);

    // P1-4: Track preview URLs in ref for proper cleanup on unmount
    // This prevents memory leaks when photos state changes during cleanup
    const previewUrlsRef = useRef<Set<string>>(new Set());

    // Validate token and get building info
    useEffect(() => {
        if (!token) {
            setError('Länken är inte giltig');
            setLoading(false);
            return;
        }

        const validateToken = async () => {
            try {
                const response = await publicReportApi.validateToken(token);
                setBuildingInfo({
                    name: response.buildingName,
                    address: response.buildingAddress,
                    publicPlacement: response.publicPlacement,
                    privacyPolicy: response.privacyPolicy,
                });
            } catch (err) {
                console.error('Token validation error:', err);
                setError('Länken är inte giltig eller har gått ut');
            } finally {
                setLoading(false);
            }
        };

        validateToken();
    }, [token]);

    // Handle dropped files
    // P2-9: Uses shared validateFile function
    const handleFilesDropped = useCallback((files: File[]) => {
        const newPhotos: UploadedPhoto[] = [];
        let currentCount = photos.length;

        for (const file of files) {
            const validation = validateFile(file, currentCount + newPhotos.length);
            if (!validation.valid) {
                if (validation.error) {
                    setValidationError(validation.error);
                }
                // Stop if max photos reached, continue for other errors
                if (validation.error?.includes('Max')) break;
                continue;
            }

            const preview = URL.createObjectURL(file);
            // P1-4: Track preview URL in ref for cleanup
            previewUrlsRef.current.add(preview);
            newPhotos.push({
                key: push(ref(getDatabase())).key!,
                file,
                preview,
                uploading: false,
                uploaded: false,
            });
        }

        if (newPhotos.length > 0) {
            setPhotos(prev => [...prev, ...newPhotos]);
        }
    }, [photos.length]);

    const { isDragOver, dragHandlers } = useDragDrop({ onFilesDropped: handleFilesDropped });

    // P1-4: Cleanup object URLs on unmount using ref
    // This avoids the stale closure problem where the effect captures old photos array
    useEffect(() => {
        const urlsRef = previewUrlsRef;
        return () => {
            urlsRef.current.forEach(url => {
                URL.revokeObjectURL(url);
            });
            urlsRef.current.clear();
        };
    }, []);

    // Upload a single photo
    const uploadPhoto = async (photo: UploadedPhoto): Promise<PublicReportPhoto | null> => {
        if (!token) return null;

        // Mark as uploading
        setPhotos(prev => prev.map(p =>
            p.key === photo.key ? { ...p, uploading: true, error: undefined } : p
        ));

        try {
            // Get image dimensions
            const dimensions = await getImageDimensions(photo.file);

            // Get signed upload URL
            const { uploadUrl, key, storagePath } = await publicReportApi.getUploadUrl(
                token,
                issueKey,
                photo.file.name,
                photo.file.type
            );

            // Upload to signed URL
            const uploadResponse = await fetch(uploadUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': photo.file.type,
                },
                body: photo.file,
            });

            if (!uploadResponse.ok) {
                throw new Error('Uppladdningen misslyckades');
            }

            // Mark as uploaded
            setPhotos(prev => prev.map(p =>
                p.key === photo.key ? {
                    ...p,
                    uploading: false,
                    uploaded: true,
                    storagePath,
                    width: dimensions.width,
                    height: dimensions.height,
                } : p
            ));

            return {
                key,
                storagePath,
                width: dimensions.width,
                height: dimensions.height,
                fileSize: photo.file.size,
            };

        } catch (err) {
            console.error('Photo upload error:', err);
            const message = err instanceof Error ? err.message : 'Uppladdningen misslyckades';
            setPhotos(prev => prev.map(p =>
                p.key === photo.key ? {
                    ...p,
                    uploading: false,
                    error: message
                } : p
            ));
            return null;
        }
    };

    // Remove a photo
    const removePhoto = (key: string) => {
        setPhotos(prev => {
            const photo = prev.find(p => p.key === key);
            if (photo?.preview) {
                URL.revokeObjectURL(photo.preview);
                // P1-4: Also remove from tracking ref
                previewUrlsRef.current.delete(photo.preview);
            }
            return prev.filter(p => p.key !== key);
        });
    };

    // Submit report
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setValidationError(null);

        // Validate description
        const trimmedDescription = description.trim();
        if (trimmedDescription.length < LIMITS.MIN_DESCRIPTION_LENGTH) {
            setValidationError(`Beskrivningen måste vara minst ${LIMITS.MIN_DESCRIPTION_LENGTH} tecken`);
            return;
        }

        if (trimmedDescription.length > LIMITS.MAX_DESCRIPTION_LENGTH) {
            setValidationError(`Beskrivningen får max vara ${LIMITS.MAX_DESCRIPTION_LENGTH} tecken`);
            return;
        }

        if (!token) {
            setValidationError('Ogiltig länk');
            return;
        }

        setSubmitting(true);

        try {
            // P2-7: Upload all photos in parallel for better performance
            const uploadPromises = photos.map(async (photo): Promise<PublicReportPhoto | null> => {
                if (photo.uploaded && photo.storagePath && photo.width && photo.height) {
                    // Already uploaded
                    return {
                        key: photo.key,
                        storagePath: photo.storagePath,
                        width: photo.width,
                        height: photo.height,
                        fileSize: photo.file.size,
                    };
                } else if (!photo.error) {
                    // Need to upload
                    return uploadPhoto(photo);
                }
                return null;
            });

            const uploadResults = await Promise.all(uploadPromises);
            const uploadedPhotos = uploadResults.filter((p): p is PublicReportPhoto => p !== null);

            // Submit the report
            await publicReportApi.submitReport(
                token,
                issueKey,
                trimmedDescription,
                uploadedPhotos.length > 0 ? uploadedPhotos : undefined
            );

            setSubmitted(true);

        } catch (err) {
            console.error('Submit error:', err);
            const message = err instanceof Error ? err.message : 'Något gick fel. Försök igen.';
            setValidationError(message);
        } finally {
            setSubmitting(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="public-report-page public-report-page--loading">
                <Spinner animation="border" />
                <p className="mt-3 text-muted">Laddar...</p>
            </div>
        );
    }

    // Error state (invalid/expired token)
    if (error) {
        return (
            <div className="public-report-page public-report-page--error">
                <Card className="text-center">
                    <Card.Body>
                        <AlertCircle size={48} className="text-danger mb-3" />
                        <h4>Länken är inte giltig</h4>
                        <p className="text-muted">
                            Länken kan ha gått ut eller är ogiltig.
                            Kontrollera att du skannade QR-koden korrekt.
                        </p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    // Success state
    if (submitted) {
        return (
            <div className="public-report-page public-report-page--completed">
                <Card className="text-center">
                    <Card.Body>
                        <CheckCircle size={48} className="text-success mb-3" />
                        <h4>Tack för din felanmälan!</h4>
                        <p className="text-muted">
                            Vi har tagit emot din rapport och kommer att hantera den så snart som möjligt.
                        </p>
                        <p className="text-muted small">
                            Du kan stänga den här sidan.
                        </p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    // Main form
    return (
        <div className="public-report-page">
            <Container className="public-report-page__container">
                {/* Header */}
                <div className="public-report-page__header">
                    <h1 className="public-report-page__title">Felanmälan</h1>
                </div>

                {/* Building info */}
                <Card className="mb-4">
                    <Card.Body>
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <Building size={20} className="text-muted" />
                            <strong>{buildingInfo?.name}</strong>
                        </div>
                        {buildingInfo?.address && (
                            <p className="text-muted mb-0 small">{buildingInfo.address}</p>
                        )}
                    </Card.Body>
                </Card>

                {/* Report form */}
                <Card>
                    <Card.Header>Beskriv problemet</Card.Header>
                    <Card.Body>
                        <Form onSubmit={handleSubmit}>
                            {/* Description */}
                            <Form.Group className="mb-4">
                                <Form.Label className="d-flex align-items-center gap-2">
                                    <MessageSquare size={18} />
                                    Beskrivning <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Beskriv problemet så detaljerat som möjligt..."
                                    disabled={submitting}
                                    maxLength={LIMITS.MAX_DESCRIPTION_LENGTH}
                                    isInvalid={description.trim().length > 0 && description.trim().length < LIMITS.MIN_DESCRIPTION_LENGTH}
                                />
                                {description.trim().length > 0 && description.trim().length < LIMITS.MIN_DESCRIPTION_LENGTH ? (
                                    <Form.Text className="text-danger">
                                        <strong>{LIMITS.MIN_DESCRIPTION_LENGTH - description.trim().length} tecken till</strong> (minst {LIMITS.MIN_DESCRIPTION_LENGTH} tecken krävs)
                                    </Form.Text>
                                ) : (
                                    <Form.Text className="text-muted">
                                        {description.length}/{LIMITS.MAX_DESCRIPTION_LENGTH} tecken (minst {LIMITS.MIN_DESCRIPTION_LENGTH})
                                    </Form.Text>
                                )}
                            </Form.Group>

                            {/* Photo upload */}
                            <div
                                {...dragHandlers}
                                style={{
                                    padding: '12px',
                                    marginBottom: '1rem',
                                    borderRadius: '8px',
                                    border: isDragOver ? '2px dashed #007bff' : '2px dashed #dee2e6',
                                    backgroundColor: isDragOver ? '#f0f8ff' : 'transparent',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <Form.Group>
                                    <Form.Label className="d-flex align-items-center gap-2">
                                        <Camera size={18} />
                                        Bilder (valfritt)
                                    </Form.Label>

                                    {/* Photo previews */}
                                    {photos.length > 0 && (
                                        <div className="public-report-page__photos mb-3">
                                            {photos.map((photo) => (
                                                <div key={photo.key} className="public-report-page__photo">
                                                    <img src={photo.preview} alt="Förhandsgranskning" />
                                                    {photo.uploading && (
                                                        <div className="public-report-page__photo-overlay">
                                                            <Spinner animation="border" size="sm" />
                                                        </div>
                                                    )}
                                                    {photo.uploaded && (
                                                        <div className="public-report-page__photo-check">
                                                            <CheckCircle size={20} />
                                                        </div>
                                                    )}
                                                    {photo.error && (
                                                        <div className="public-report-page__photo-error">
                                                            <AlertCircle size={16} />
                                                        </div>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="public-report-page__photo-remove"
                                                        onClick={() => removePhoto(photo.key)}
                                                        disabled={photo.uploading}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Add photo button */}
                                    {photos.length < LIMITS.MAX_PHOTOS && (
                                        <button
                                            type="button"
                                            className="public-report-page__add-photo"
                                            onClick={() => setShowUploadModal(true)}
                                            disabled={submitting}
                                        >
                                            <Upload size={20} />
                                            <span>Lägg till foto</span>
                                        </button>
                                    )}

                                    <UploadPickerModal
                                        show={showUploadModal}
                                        onHide={() => setShowUploadModal(false)}
                                        onFilesSelected={handleFilesDropped}
                                        accept={ALLOWED_MIME_TYPES.join(',')}
                                        title="Lägg till foto"
                                        multiple
                                    />

                                    <Form.Text className="text-muted d-block mt-2">
                                        Max {LIMITS.MAX_PHOTOS} bilder, 10 MB per bild. Du kan även dra och släppa bilder här.
                                    </Form.Text>
                                </Form.Group>
                            </div>

                            {/* Privacy notice, only for codes in public places */}
                            {buildingInfo?.publicPlacement && (
                                <PublicReportPrivacyNotice policy={buildingInfo.privacyPolicy} />
                            )}

                            {/* Validation error */}
                            {validationError && (
                                <Alert variant="danger" dismissible onClose={() => setValidationError(null)} className="mb-3">
                                    {validationError}
                                </Alert>
                            )}

                            {/* Submit button */}
                            <Button
                                type="submit"
                                variant="primary"
                                size="lg"
                                className="w-100"
                                disabled={submitting || description.trim().length < LIMITS.MIN_DESCRIPTION_LENGTH}
                            >
                                {submitting ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Skickar...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle size={20} className="me-2" />
                                        Skicka felanmälan
                                    </>
                                )}
                            </Button>
                        </Form>
                    </Card.Body>
                </Card>

                {/* Footer */}
                <div className="public-report-page__footer">
                    <small className="text-muted">
                        Powered by Molnfastigheter
                    </small>
                </div>
            </Container>
        </div>
    );
};

export default PublicReportPage;
